import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Trash2, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { offlineDb, type VendaPendente } from '@/lib/offlineDb';
import { sincronizarVendasPendentes, descartarVendaPendente } from '@/lib/syncManager';

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDt = (s: string) => new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

const statusLabel: Record<string, string> = {
  Pendente:          'Pendente',
  Sincronizando:     'Sincronizando...',
  Sincronizada:      'Sincronizada',
  ConflitoPendente:  'Conflito',
  Descartada:        'Descartada',
};

const statusVariant: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  Pendente:         'secondary',
  Sincronizando:    'default',
  Sincronizada:     'outline',
  ConflitoPendente: 'destructive',
  Descartada:       'outline',
};

export default function SincronizacaoOffline() {
  const [vendas,           setVendas]           = useState<VendaPendente[]>([]);
  const [sincronizando,    setSincronizando]    = useState(false);
  const [ultimaSync,       setUltimaSync]       = useState<string | null>(null);
  const [confirmDescartar, setConfirmDescartar] = useState<string | null>(null);

  async function carregar() {
    const todas = await offlineDb.obterVendasPendentes();
    todas.sort((a, b) => b.dtCriacao.localeCompare(a.dtCriacao));
    setVendas(todas);
  }

  useEffect(() => {
    carregar();
  }, []);

  async function handleSincronizar() {
    if (!navigator.onLine) {
      toast.warning('Sem conexão com o servidor.');
      return;
    }
    setSincronizando(true);
    try {
      await sincronizarVendasPendentes();
      setUltimaSync(new Date().toLocaleString('pt-BR'));
      toast.success('Sincronização concluída.');
    } catch {
      toast.error('Falha na sincronização.');
    } finally {
      setSincronizando(false);
      await carregar();
    }
  }

  async function handleDescartar(idLocal: string) {
    await descartarVendaPendente(idLocal);
    setConfirmDescartar(null);
    toast.info('Venda descartada da fila.');
    await carregar();
  }

  const pendentes = vendas.filter(v => v.status === 'Pendente' || v.status === 'Sincronizando');
  const conflitos = vendas.filter(v => v.status === 'ConflitoPendente');

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sincronização Offline</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vendas salvas localmente durante período sem conexão.
          </p>
        </div>
        <Button onClick={handleSincronizar} disabled={sincronizando || !navigator.onLine}>
          <RefreshCw className={`mr-2 h-4 w-4 ${sincronizando ? 'animate-spin' : ''}`} />
          {sincronizando ? 'Sincronizando...' : 'Sincronizar Agora'}
        </Button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold">{pendentes.length}</p>
            <p className="text-xs text-muted-foreground mt-1">Pendentes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-destructive">{conflitos.length}</p>
            <p className="text-xs text-muted-foreground mt-1">Com conflito</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-sm font-medium">{ultimaSync ?? '—'}</p>
            <p className="text-xs text-muted-foreground mt-1">Última sync</p>
          </CardContent>
        </Card>
      </div>

      {/* Lista */}
      {vendas.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center space-y-2">
              <CheckCircle className="mx-auto h-10 w-10 text-green-500" />
              <p className="text-sm text-muted-foreground">Nenhuma venda pendente na fila.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {vendas.map((v) => (
            <Card key={v.idLocal} className={v.status === 'ConflitoPendente' ? 'border-destructive/50' : ''}>
              <CardHeader className="pb-2 pt-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-mono text-muted-foreground">
                    #{v.idLocal.slice(0, 8)}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant={statusVariant[v.status] ?? 'secondary'}>
                      {statusLabel[v.status] ?? v.status}
                    </Badge>
                    {(v.status === 'Pendente' || v.status === 'ConflitoPendente') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => setConfirmDescartar(v.idLocal)}
                        title="Descartar venda"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pb-3 space-y-2">
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {fmtDt(v.dtCriacao)}
                  </span>
                  {v.tentativas > 0 && (
                    <span>{v.tentativas} tentativa{v.tentativas > 1 ? 's' : ''}</span>
                  )}
                </div>

                {v.erroSync && (
                  <div className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    {v.erroSync}
                  </div>
                )}

                <Separator />

                <div className="space-y-1 text-sm">
                  {v.itens.map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span className="text-muted-foreground">{item.descricao} × {item.quantidade}</span>
                      <span>{fmt(item.precoUnitario * item.quantidade)}</span>
                    </div>
                  ))}
                </div>

                <Separator />

                <div className="flex justify-between text-sm font-semibold">
                  <span>Total</span>
                  <span>{fmt(v.totalLiquido)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Confirm descartar */}
      <Dialog open={!!confirmDescartar} onOpenChange={(o) => !o && setConfirmDescartar(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Descartar Venda
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            A venda será removida da fila local e não será enviada ao servidor. Esta ação não pode ser desfeita.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDescartar(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => confirmDescartar && handleDescartar(confirmDescartar)}>
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
